import {PaginationData} from '_components/admin/admin.types';
import {Prescriber} from '_redux/admin/prescribers/admin-prescriber-model.types';
import {Therapist} from '_redux/admin/therapists/admin-therapist-model.types';
import {
  AvailabilityInfoSlot,
  FetchedClientsResponse,
  NormalizedFetchedPrescribers,
  NormalizedFetchedProviders,
  NormalizedFetchedTherapists,
  Provider,
  ProvidersModelTypes,
  QueryKeysType,
} from '_redux/calendar/calendar.types';
import requestHandler from '_services/api/axios-config';
import {HttpMethods} from '_services/api/axios.types';
import {
  addDays,
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  isValid,
  parse,
  parseISO,
  startOfMonth,
  startOfToday,
  startOfTomorrow,
  startOfYear,
  subMonths,
} from 'date-fns';
import {format as formatTZ, utcToZonedTime, zonedTimeToUtc} from 'date-fns-tz';
import {schema} from 'normalizr';
import {Modifier} from 'react-day-picker';
import tinycolor from 'tinycolor2';
import {
  AppointmentDurations,
  AppointmentTypes,
  DateRange,
  FetchProvidersResponse,
  InitialDateOptions,
  PresetRange,
  SelectOption,
  SessionType,
} from './modal.types';
import {createNormalizedPayload} from './normalize-utils';
import {prescriberEntity, therapistEntity} from './schema';

export const doctorSessions: SessionType[] = [
  AppointmentDurations.ten,
  AppointmentDurations.thirty,
].map(duration => ({
  label: `${duration} min`,
  value: duration,
  type: AppointmentTypes.doctor_consultation,
}));

export const therapySessions: SessionType[] = [
  AppointmentDurations.thirty,
  AppointmentDurations.forty_five,
  AppointmentDurations.sixty,
].map(duration => ({
  label: `${duration} min`,
  value: duration,
  type: AppointmentTypes.video_call_with_therapist,
}));

const selectedSessions: Record<AppointmentTypes, SessionType[]> = {
  [AppointmentTypes.doctor_consultation]: doctorSessions,
  [AppointmentTypes.video_call_with_therapist]: therapySessions,
};

export function getIdForBoth(provider: Therapist | Prescriber): string {
  if ('therapistId' in provider) return provider.therapistId;
  if ('prescriberId' in provider) return provider.prescriberId;
  return '';
}

export const getSessions = (apptType: AppointmentTypes) => {
  return apptType ? selectedSessions[apptType] : [];
};

export const MAX_PROVIDERS_PER_PAGE = 20;
export const MAX_PATIENTS_PER_PAGE = 20;

const paginationDefault = {
  currentPage: 1,
  resultsPerPage: MAX_PROVIDERS_PER_PAGE,
  totalResults: 0,
  totalPages: 0,
  hasMore: false,
};

type FetchProvidersOptions<T> = {
  entitySchema?:
    | schema.Entity<T>
    | {
        therapist: typeof therapistEntity; // schema.Entity<Therapist>
        prescriber: typeof prescriberEntity; // schema.Entity<Prescriber>
      };
  filter?: ProvidersModelTypes;
  getId?: (provider: T) => string;
  onSuccess?: (
    payload:
      | NormalizedFetchedPrescribers
      | NormalizedFetchedTherapists
      | NormalizedFetchedProviders,
  ) => void;
  providers?: boolean;
};

export function doFetchProvidersOptions<T extends Provider>(
  endpoint: string,
  search: string,
  page: number,
  countryOfResidenceCode?: string,
  role?: string,
  options: FetchProvidersOptions<T> = {},
): Promise<{
  options: SelectOption[];
  hasMore: boolean;
  pagination: PaginationData;
}> {
  const {
    entitySchema = {
      therapist: therapistEntity,
      prescriber: prescriberEntity,
    },
    filter = ProvidersModelTypes.therapists,
    getId = () => '',
    onSuccess = () => {},
    providers = false,
  } = options;

  const url = `${endpoint}?searchKeywords=${search}&maxResults=${MAX_PROVIDERS_PER_PAGE}&page=${page}&countryCode=${countryOfResidenceCode ??
    ''}&role=${role}`;

  return requestHandler<FetchProvidersResponse<T>>({
    method: HttpMethods.GET,
    url,
  }).then(response => {
    const {results, ...rest} = response.data.message;
    const pagination = rest as PaginationData;

    if (results.length === 0) {
      return {
        options: [],
        hasMore: false,
        pagination: pagination ?? paginationDefault,
      };
    }

    if (providers) {
      // Fetch both therapists and prescribers
      const therapists = results.filter(
        r => r.role === 'therapist',
      ) as Therapist[];
      const prescribers = results.filter(
        r => r.role === 'prescriber',
      ) as Prescriber[];

      const normalizedTherapistsPayload = createNormalizedPayload(
        therapists,
        (entitySchema as {
          therapist: typeof therapistEntity;
        }).therapist, // Use the therapist schema from options
        ProvidersModelTypes.therapists,
        pagination,
      );

      const normalizedPrescribersPayload = createNormalizedPayload(
        prescribers,
        (entitySchema as {
          prescriber: typeof prescriberEntity;
        }).prescriber, // Use the prescriber schema from options
        ProvidersModelTypes.prescribers,
        pagination,
      );

      const normalizedPayload: NormalizedFetchedProviders = {
        entities: {
          therapists: normalizedTherapistsPayload.entities.therapists,
          prescribers: normalizedPrescribersPayload.entities.prescribers,
        },
        pagination,
        result: normalizedTherapistsPayload.result.concat(
          normalizedPrescribersPayload.result,
        ),
        filter,
      };

      onSuccess(normalizedPayload);
    } else {
      // Handle single type fetch (either therapists or prescribers)
      if (filter === ProvidersModelTypes.therapists) {
        const normalizedPayload = createNormalizedPayload(
          results as Therapist[],
          entitySchema as typeof therapistEntity,
          filter,
          pagination,
        );
        onSuccess(normalizedPayload);
      } else {
        const normalizedPayload = createNormalizedPayload(
          results as Prescriber[],
          entitySchema as typeof prescriberEntity,
          filter,
          pagination,
        );
        onSuccess(normalizedPayload);
      }
    }

    // Map options for the select component
    const selectOptions: SelectOption[] = results.map(provider => ({
      label: providers
        ? `${provider.fullName} - ${provider.role}`
        : provider.fullName,
      value: providers
        ? getIdForBoth(provider as Therapist | Prescriber)
        : getId(provider),
    }));

    return {
      options: selectOptions,
      hasMore: pagination.hasMore ?? false,
      pagination: pagination ?? paginationDefault,
    };
  });
}

export const doFetchPatientsEmailOptions = (
  search: string,
  page: number,
  onSuccess?: (payload: FetchedClientsResponse) => void,
  countryOfResidenceCode?: string,
) => {
  const url = `/api/admin/patients/paginated-patients?search=${search}&maxResults=${MAX_PATIENTS_PER_PAGE}&page=${page}&countryCode=${countryOfResidenceCode ??
    ''}`;
  return requestHandler<{
    message: FetchedClientsResponse;
  }>({
    method: HttpMethods.GET,
    url,
  }).then(response => {
    const {results, pagination} = response.data.message;

    if (!results || results.length === 0) {
      return {
        options: [] as SelectOption[],
        hasMore: false,
        pagination: {
          currentPage: 1,
          resultsPerPage: MAX_PATIENTS_PER_PAGE,
          totalResults: 0,
          totalPages: 0,
          hasMore: false,
        },
      };
    }

    onSuccess?.(response.data.message);
    return {
      options: results.map(patient => ({
        label: patient.email,
        value: patient.email,
      })),
      hasMore: pagination.hasMore,
      pagination,
    };
  });
};

export const isValidEmail = (email: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);

export function getDisabledDays(
  availableSlots: AvailabilityInfoSlot[],
  isProvider: boolean,
): Modifier | Modifier[] {
  const divider = isProvider ? startOfToday() : startOfTomorrow();

  // disable everything for empty availability
  if (availableSlots.length === 0) return [{before: divider, after: divider}];

  const sortedDates = availableSlots
    .map(slot => parseISO(slot.availabilityDate))
    .sort((a, b) => a.getTime() - b.getTime());

  const firstAvailableDate: Date = sortedDates[0];
  const lastAvailableDate: Date = sortedDates[sortedDates.length - 1];

  const rangeModifier: Modifier = {before: divider, after: lastAvailableDate};

  // generate full month between first and last day
  const monthStart = startOfMonth(firstAvailableDate);
  const monthEnd = endOfMonth(lastAvailableDate);
  const fullMonthDates = eachDayOfInterval({start: monthStart, end: monthEnd});

  const availableDates = new Set(
    sortedDates.map(date => format(date, 'yyyy-MM-dd')),
  );

  const unavailableDates: Date[] = fullMonthDates.filter(date => {
    const formattedDate = format(date, 'yyyy-MM-dd');
    return !availableDates.has(formattedDate);
  });

  const result: Modifier | Modifier[] = [rangeModifier];

  for (let i = 0; i < unavailableDates.length; i++) {
    result.push(unavailableDates[i]);
  }

  return result;
}

export const dateTimeStringCompatibility = (date: string): string => {
  const parsedDate = parseISO(date);
  const formattedDate = format(parsedDate, 'yyyy/MM/dd');
  return `${formattedDate} 00:00:00`;
};

export const convertDateToDiffTimeZone = (
  time: string,
  timeZone: string,
): string => {
  const utcDate = zonedTimeToUtc(time, 'UTC');
  const zonedDate = utcToZonedTime(utcDate, timeZone);
  return formatTZ(zonedDate, 'h:mm aaaa', {timeZone});
};

export const getAvailableTimeData = (
  timeZone: string,
  availableTimes: string[],
): {value: string; rawTime: string; disabled: boolean}[] =>
  availableTimes.map(time => ({
    value: convertDateToDiffTimeZone(time, timeZone),
    rawTime: time,
    disabled: false,
  }));

// all the date-range options
export const DATE_RANGE_START = new Date(2019, 0, 1);

export const formatDate = (date?: Date) =>
  date ? format(date, 'MM/dd/yyyy') : '';

export const addDayAndFormat = (date?: Date, addDay = 1) =>
  date ? format(addDays(date, addDay), 'MM/dd/yyyy') : '';

export const AllDateRangeOptions: InitialDateOptions[] = [
  {label: 'All Time', value: PresetRange.AllTime, active: false},
  {label: 'This Year', value: PresetRange.CurrentYear, active: false},
  {label: 'Last 6 Months', value: PresetRange.Last6Months, active: false},
  {label: 'Last 3 Months', value: PresetRange.Last3Months, active: false},
  {label: 'Last Month', value: PresetRange.LastMonth, active: false},
  {label: 'This Month', value: PresetRange.CurrentMonth, active: false},
  {label: 'Next Month', value: PresetRange.NextMonth, active: false},
  {label: 'Next 3 Months', value: PresetRange.Next3Months, active: false},
];

export const PresetLabelsByValue = {
  [PresetRange.AllTime]: 'All Time',
  [PresetRange.CurrentYear]: 'This Year',
  [PresetRange.Last6Months]: 'Last 6 Months',
  [PresetRange.Last3Months]: 'Last 3 Months',
  [PresetRange.LastMonth]: 'Last Month',
  [PresetRange.CurrentMonth]: 'This Month',
  [PresetRange.NextMonth]: 'Next Month',
  [PresetRange.Next3Months]: 'Next 3 Months',
};

export const CSVDateRanges: PresetRange[] = [
  PresetRange.AllTime,
  PresetRange.CurrentYear,
  PresetRange.Last6Months,
  PresetRange.Last3Months,
  PresetRange.CurrentMonth,
];

export const FiletDateRanges: PresetRange[] = [
  PresetRange.Last3Months,
  PresetRange.LastMonth,
  PresetRange.CurrentMonth,
  PresetRange.NextMonth,
  PresetRange.Next3Months,
];

export const getCSVDateRangeOptions = (): InitialDateOptions[] =>
  AllDateRangeOptions.filter(dateRange =>
    CSVDateRanges.includes(dateRange.value),
  );

export const getFiletDateRangeOptions = (): InitialDateOptions[] =>
  AllDateRangeOptions.filter(dateRange =>
    FiletDateRanges.includes(dateRange.value),
  );

export const getRangeFromOptions = (
  range: PresetRange | undefined,
): DateRange => {
  const now = new Date();
  switch (range) {
    case PresetRange.AllTime:
      return {
        from: DATE_RANGE_START,
        to: now,
        label: range,
      };

    case PresetRange.CurrentYear:
      return {
        from: startOfYear(now),
        to: endOfMonth(now),
        label: range,
      };

    case PresetRange.Last6Months: {
      const from = startOfMonth(subMonths(now, 6));
      const to = endOfMonth(subMonths(now, 1));
      return {from, to, label: range};
    }

    case PresetRange.Last3Months: {
      const from = startOfMonth(subMonths(now, 3));
      const to = endOfMonth(subMonths(now, 1));
      return {from, to, label: range};
    }

    case PresetRange.LastMonth: {
      const lastMonth = subMonths(now, 1);
      return {
        from: startOfMonth(lastMonth),
        to: endOfMonth(lastMonth),
        label: range,
      };
    }

    case PresetRange.CurrentMonth:
      return {
        from: startOfMonth(now),
        to: endOfMonth(now),
        label: range,
      };

    case PresetRange.NextMonth: {
      const nextMonth = addMonths(now, 1);
      return {
        from: startOfMonth(nextMonth),
        to: endOfMonth(nextMonth),
        label: range,
      };
    }

    case PresetRange.Next3Months: {
      const from = startOfMonth(addMonths(now, 1));
      const to = endOfMonth(addMonths(endOfMonth(now), 3));
      return {from, to, label: range};
    }
    default:
      return {from: undefined, to: undefined};
  }
};

export const generateDateOptions = (): SelectOption[] => {
  const now = new Date();

  const dates = eachDayOfInterval({
    start: DATE_RANGE_START,
    end: now,
  }).reverse();
  return dates.map(date => {
    const formatted = formatDate(date);
    return {label: formatted, value: formatted};
  });
};

export const parseDateString = (dateString: string): Date => {
  const parsedDate = parse(dateString, 'MM/dd/yyyy', new Date());
  return isValid(parsedDate) ? parsedDate : new Date();
};

export const formatLabelFromCamelCase = (key: string) =>
  key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());

// Labels options
export const acuityLabelsId = {
  cancelled24Hours: 7015759,
  completed: 365545,
  dpoop: 11378348,
  dpoopCompleted: 15553617,
  dpInNetworkCompleted: 14880302,
  freeNoShow: 7867786,
  freeSession: 7644596,
  inNetwork: 8289171,
  mSmessup: 7198909,
  noShow: 3929266,
  paperworkAppt: 8065762,
  providerCancel: 7327699,
  providerFree: 7336636,
  providerMissed: 4424698,
  rescheduled: 4623132,
  techIssue: 9119729,
  techIssueFree: 7198995,
  interested: 12690056,
  notInterested: 12690075,
  joined: 12690076,
  testAccount: 9898034,
  shortDuration: 14733703,
};

export const acuityLabelsName = {
  cancelled24Hours: 'cancelled24Hours',
  completed: 'completed',
  dpoop: 'dpoop',
  dpoopCompleted: 'dpoopCompleted',
  dpInNetworkCompleted: 'dpInNetworkCompleted',
  freeNoShow: 'freeNoShow',
  freeSession: 'freeSession',
  inNetwork: 'inNetwork',
  mSmessup: 'msMessup', //mSmessup
  noShow: 'noShow',
  paperworkAppt: 'paperworkAppt',
  providerCancel: 'providerCancel',
  providerFree: 'providerFree',
  providerMissed: 'providerMissed',
  rescheduled: 'rescheduled',
  techIssue: 'techIssue',
  techIssueFree: 'techIssueFree',
  interested: 'interested',
  notInterested: 'notInterested',
  joined: 'joined',
  testAccount: 'testAccount',
  shortDuration: 'shortDuration',
};

export type LabelKey = keyof typeof acuityLabelsId;

export const selectLabelOptions = (Object.keys(
  acuityLabelsId,
) as LabelKey[]).map(key => ({
  label: acuityLabelsName[key],
  value: String(acuityLabelsId[key]),
}));

export function isSameQueryObj(
  obj1: QueryKeysType,
  obj2: QueryKeysType,
): boolean {
  const keys1 = Object.keys(obj1) as (keyof QueryKeysType)[];
  const keys2 = Object.keys(obj2) as (keyof QueryKeysType)[];

  if (keys1.length !== keys2.length) return false;

  return keys1.every(key => obj1[key] === obj2[key]);
}

export const hasActiveFilter = (queryKeys: QueryKeysType) =>
  Object.values(queryKeys).some(value => value !== '');

export const convertToISOWithOffset = (
  time: string,
  timeZone: string,
): string => {
  const zonedDate = utcToZonedTime(time, timeZone);
  return formatTZ(zonedDate, "yyyy-MM-dd'T'HH:mm:ssxx", {timeZone});
};

const normalizeOffset = (s: string) =>
  s.replace(/([+-]\d{2})(\d{2})$/, '$1:$2');

export const toDateWithoutOffset = (isoLike: string) => new Date(isoLike);

export const toDate = (isoLike: string) => new Date(normalizeOffset(isoLike));

// Format a Date in the patient's timezone if date-fns-tz is available, else fall back to local time.
export const fmtTime = (d: Date, tz: string) => {
  const s = formatTZ(d, '(O) z', {timeZone: tz || 'UTC'}); // e.g. "(GMT-7) PDT"
  return s.replace(/\(GMT([+-])/, '(GMT $1'); // -> "2:20 AM (GMT -7)"
};

export function formatIgnoringTZ(iso: string, outFmt = 'yyyy-MM-dd HH:mm') {
  // Remove trailing "Z" or any offset like -700 / -07:00
  const core = iso.replace(/(Z|[+-]\d{1,2}:?\d{2})$/, '');
  // Parse as LOCAL time (no timezone math)
  const d = parse(core, "yyyy-MM-dd'T'HH:mm:ss", new Date());
  return format(d, outFmt);
}

/**
 * Calculate the start date for a recurring booking based on day of week
 * @param dayOfWeek - Day abbreviation (M, T, W, Th, F, S, Su)
 * @param fromDate - Optional base date (defaults to today)
 * @returns ISO date string for the next occurrence of the specified day
 */
export function calculateRecurringStartDate(
  dayOfWeek: string,
  fromDate?: Date,
): string {
  const dayMap: Record<string, number> = {
    Su: 0,
    M: 1,
    T: 2,
    W: 3,
    Th: 4,
    F: 5,
    S: 6,
  };

  const targetDay = dayMap[dayOfWeek];
  if (targetDay === undefined) {
    throw new Error(`Invalid day of week: ${dayOfWeek}`);
  }

  const baseDate = fromDate || new Date();
  const currentDay = baseDate.getDay();

  let daysUntilTarget = targetDay - currentDay;

  if (daysUntilTarget <= 0) {
    daysUntilTarget += 7;
  }

  const startDate = new Date(baseDate);
  startDate.setDate(baseDate.getDate() + daysUntilTarget);

  return startDate.toISOString().split('T')[0];
}

/**
 * Format time slots for display in provider's timezone
 * @param timeSlots - Array of time strings with timezone offset (e.g., "17:30:00.000-07:00")
 * @param providerTimezone - Provider's timezone for conversion
 * @param referenceDate - Optional date to use for DST-aware formatting (defaults to today)
 * @returns Formatted time slots for display
 */
export function formatRecurringTimeSlots(
  timeSlots: string[],
  providerTimezone?: string,
  referenceDate?: string,
): Array<{value: string; label: string}> {
  return timeSlots.map(timeSlot => {
    if (
      providerTimezone &&
      (timeSlot.includes('-') || timeSlot.includes('+'))
    ) {
      // Time slot already has timezone offset (e.g., "17:30:00.000-07:00")
      const dateString = getReferenceDateString(referenceDate);
      const isoString = `${dateString}T${timeSlot}`;

      const shown = formatTZ(
        utcToZonedTime(new Date(isoString), providerTimezone),
        'h:mm a',
        {timeZone: providerTimezone},
      );

      return {
        value: timeSlot,
        label: shown,
      };
    } else {
      const timeOnly = timeSlot.split('.')[0]; // Remove milliseconds if present
      const [hours, minutes] = timeOnly.split(':');
      const hour24 = parseInt(hours, 10);
      const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
      const ampm = hour24 >= 12 ? 'PM' : 'AM';

      const displayTime = `${hour12}:${minutes} ${ampm}`;

      return {
        value: timeSlot,
        label: displayTime,
      };
    }
  });
}

/**
 * Safely formats only the date part (before 'T') of an ISO string to MM/dd/yyyy.
 */
export const formatDateWithoutTimezone = (dateTimeString: string) => {
  try {
    if (typeof dateTimeString !== 'string') return '';
    const dateOnly = dateTimeString.split('T')[0];
    const parsed = parseISO(dateOnly);
    if (!isValid(parsed)) return '';
    return format(parsed, 'MM/dd/yyyy');
  } catch {
    return '';
  }
};

/**
 * This ensures timezone abbreviations (PDT/PST, EDT/EST) are correct for DST transitions.
 * @param recurringStartDate - Optional recurring appointment start date
 * @returns Date string in YYYY-MM-DD format
 */
export const getReferenceDateString = (recurringStartDate?: string): string => {
  return recurringStartDate
    ? new Date(recurringStartDate).toISOString().split('T')[0]
    : new Date().toISOString().split('T')[0];
};

// Determine readable text color based on the background
export function getContrastAndBackgroundColor(
  bgColor: string,
  fallbackColor = '#F54927',
): {textColor: 'black' | 'white'; backgroundColor: string} {
  const color = tinycolor(bgColor);

  // If invalid color, fallback to a default background
  const backgroundColor = color.isValid() ? bgColor : fallbackColor;

  // Determine readable text color based on the background
  const textColor = tinycolor(backgroundColor).isLight() ? 'black' : 'white';

  return {textColor, backgroundColor};
}
